import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var21');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('image-var21');
            imageHolder.appendChild(leftImage);

            const leftWatermark = createWatermarkTagImage(
              'left-actual-photo-var21',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftWatermark) imageHolder.appendChild(leftWatermark);

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('image-var21');
            imageHolder.appendChild(rightImage);

            const rightWatermark = createWatermarkTagImage(
              'right-actual-photo-var21',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightWatermark) imageHolder.appendChild(rightWatermark);

            if (variation.rightText) {
              const textHolder = document.createElement('div');
              const paragraph = document.createElement('p');

              textHolder.classList.add('text-var21');

              const updatedText = replaceQuotes(variation.rightText);

              let text = updatedText;

              if (variation.brakes) {
                paragraph.innerText = text;
                paragraph.classList.add('custom-brakes');
              } else {
                paragraph.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 880,
                  fontFamily: 'SourceSansProBold',
                  minFontSize: 45,
                });
              }

              textHolder.append(paragraph);

              imageHolder.appendChild(textHolder);
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var21',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
